/**
 * Dev Bridge — mock data dla ?dev=true. Port z js/devBridge.js (1:1).
 * Aktywne tylko gdy IS_DEV_MODE === true. W produkcji nieużywane.
 */

import { IS_DEV_MODE } from './firebase';

export interface DevUser {
  uid: string;
  name: string;
  email: string;
}

export interface DevFamilyMember {
  name: string;
  email: string;
  joinedAt: Date;
}

export interface DevFamily {
  id: string;
  name: string;
  inviteCode: string;
  createdAt: Date;
  members: Record<string, DevFamilyMember>;
}

export interface DevWallet {
  id: string;
  familyId: string;
  ownerId: string;
  name: string;
  type: 'shared' | 'personal';
  color: string;
  createdAt: Date;
}

export interface DevCategory {
  id: string;
  familyId: string;
  name: string;
  icon: string;
  color: string;
  type: 'income' | 'expense';
  isDefault: boolean;
  order: number;
}

export interface DevTransaction {
  id: string;
  familyId: string;
  walletId: string;
  toWalletId?: string;
  userId: string;
  type: 'income' | 'expense' | 'transfer';
  amount: number;
  categoryId: string | null;
  note: string;
  date: Date;
  isRecurring: boolean;
  recurringInterval?: string | null;
  recurringEvery?: number;
  recurringUnit?: string;
  recurringEndDate?: Date | null;
  isPaused?: boolean;
  createdAt: Date;
  syncStatus: 'synced' | 'pending';
}

export interface DevBudget {
  id: string;
  familyId: string;
  walletId: string;
  categoryId: string;
  amount: number;
  period: 'monthly' | 'weekly' | 'yearly';
  alertAt: number;
}

export interface DevGoalDeposit {
  id: string;
  amount: number;
  date: Date;
  type: 'manual' | 'wallet';
  walletId: string | null;
}

export interface DevGoal {
  id: string;
  familyId: string;
  name: string;
  icon: string;
  color: string;
  target: number;
  deadline: Date | null;
  createdAt: Date;
  deposits: DevGoalDeposit[];
}

// ── Mock user ─────────────────────────────────
export const DEV_USER: DevUser = {
  uid: 'dev-user-1',
  name: 'Damian',
  email: 'dev@test.pl',
};

// ── Mock family ───────────────────────────────
export const DEV_FAMILY: DevFamily = {
  id: 'dev-family-1',
  name: 'Rodzina Kowalskich',
  inviteCode: 'DEV123',
  createdAt: new Date('2026-01-01'),
  members: {
    'dev-user-1': { name: 'Damian', email: 'dev@test.pl', joinedAt: new Date('2026-01-01') },
    'dev-user-2': { name: 'Ania', email: 'ania@test.pl', joinedAt: new Date('2026-01-02') },
  },
};

// ── Mock wallets ──────────────────────────────
export const DEV_WALLETS: DevWallet[] = [
  { id: 'dev-wallet-shared', familyId: 'dev-family-1', ownerId: 'shared', name: 'Wspólny', type: 'shared', color: '#E53935', createdAt: new Date('2026-01-01') },
  { id: 'dev-wallet-damian', familyId: 'dev-family-1', ownerId: 'dev-user-1', name: 'Damian', type: 'personal', color: '#1976D2', createdAt: new Date('2026-01-01') },
  { id: 'dev-wallet-ania', familyId: 'dev-family-1', ownerId: 'dev-user-2', name: 'Ania', type: 'personal', color: '#7B1FA2', createdAt: new Date('2026-01-02') },
];

// ── Mock categories (V2) ──────────────────────
export const DEV_CATEGORIES: DevCategory[] = [
  // Wydatki
  { id: 'cat-food', familyId: 'dev-family-1', name: 'Zakupy spożywcze', icon: 'shopping_cart', color: '#4CAF50', type: 'expense', isDefault: true, order: 1 },
  { id: 'cat-resto', familyId: 'dev-family-1', name: 'Restauracje', icon: 'restaurant', color: '#FF9800', type: 'expense', isDefault: true, order: 2 },
  { id: 'cat-shopping', familyId: 'dev-family-1', name: 'Zakupy', icon: 'shopping_bag', color: '#E91E63', type: 'expense', isDefault: true, order: 3 },
  { id: 'cat-car', familyId: 'dev-family-1', name: 'Transport', icon: 'car', color: '#2196F3', type: 'expense', isDefault: true, order: 4 },
  { id: 'cat-health', familyId: 'dev-family-1', name: 'Zdrowie', icon: 'health', color: '#F44336', type: 'expense', isDefault: true, order: 5 },
  { id: 'cat-home', familyId: 'dev-family-1', name: 'Dom', icon: 'home', color: '#795548', type: 'expense', isDefault: true, order: 6 },
  { id: 'cat-bills', familyId: 'dev-family-1', name: 'Rachunki', icon: 'bills', color: '#607D8B', type: 'expense', isDefault: true, order: 7 },
  { id: 'cat-child', familyId: 'dev-family-1', name: 'Dzieci', icon: 'child', color: '#FF4081', type: 'expense', isDefault: true, order: 8 },
  { id: 'cat-entert', familyId: 'dev-family-1', name: 'Rozrywka', icon: 'movie', color: '#9C27B0', type: 'expense', isDefault: true, order: 9 },
  { id: 'cat-sport', familyId: 'dev-family-1', name: 'Sport i hobby', icon: 'sport', color: '#00BCD4', type: 'expense', isDefault: true, order: 10 },
  { id: 'cat-beauty', familyId: 'dev-family-1', name: 'Uroda', icon: 'sparkles', color: '#EC407A', type: 'expense', isDefault: true, order: 11 },
  { id: 'cat-travel', familyId: 'dev-family-1', name: 'Podróże', icon: 'travel', color: '#039BE5', type: 'expense', isDefault: true, order: 12 },
  { id: 'cat-education', familyId: 'dev-family-1', name: 'Edukacja', icon: 'education', color: '#3F51B5', type: 'expense', isDefault: true, order: 13 },
  { id: 'cat-gifts', familyId: 'dev-family-1', name: 'Prezenty', icon: 'gift', color: '#FFC107', type: 'expense', isDefault: true, order: 14 },
  { id: 'cat-pets', familyId: 'dev-family-1', name: 'Zwierzęta', icon: 'pets', color: '#8D6E63', type: 'expense', isDefault: true, order: 15 },
  { id: 'cat-invest', familyId: 'dev-family-1', name: 'Inwestycje', icon: 'trending_up', color: '#43A047', type: 'expense', isDefault: true, order: 16 },
  { id: 'cat-other', familyId: 'dev-family-1', name: 'Inne', icon: 'more', color: '#9E9E9E', type: 'expense', isDefault: true, order: 17 },
  // Przychody
  { id: 'cat-salary', familyId: 'dev-family-1', name: 'Wynagrodzenie', icon: 'banknote', color: '#4CAF50', type: 'income', isDefault: true, order: 1 },
  { id: 'cat-bonus', familyId: 'dev-family-1', name: 'Premia', icon: 'gift', color: '#8BC34A', type: 'income', isDefault: true, order: 2 },
  { id: 'cat-800plus', familyId: 'dev-family-1', name: '800+', icon: 'family', color: '#00BCD4', type: 'income', isDefault: true, order: 3 },
  { id: 'cat-zwrot', familyId: 'dev-family-1', name: 'Zwrot', icon: 'bank', color: '#1976D2', type: 'income', isDefault: true, order: 4 },
  { id: 'cat-inc-other', familyId: 'dev-family-1', name: 'Inne przychody', icon: 'coins', color: '#9E9E9E', type: 'income', isDefault: true, order: 5 },
];

// ── Generator transakcji ──────────────────────
function randomDateInMonth(year: number, month: number): Date {
  const day = Math.floor(Math.random() * 28) + 1;
  return new Date(year, month, day);
}

function generateMockTransactions(): DevTransaction[] {
  const txs: DevTransaction[] = [];
  const now = new Date();
  const wallets = ['dev-wallet-damian', 'dev-wallet-ania'];

  const expenseData: { note: string; cat: string; min: number; max: number }[] = [
    { note: 'Biedronka', cat: 'cat-food', min: 5000, max: 25000 },
    { note: 'Lidl', cat: 'cat-food', min: 8000, max: 40000 },
    { note: 'Pyszne.pl', cat: 'cat-resto', min: 3500, max: 12000 },
    { note: 'Paliwo', cat: 'cat-car', min: 15000, max: 35000 },
    { note: 'OC/AC', cat: 'cat-car', min: 20000, max: 60000 },
    { note: 'Apteka', cat: 'cat-health', min: 2000, max: 8000 },
    { note: 'Czynsz', cat: 'cat-home', min: 150000, max: 200000 },
    { note: 'Prąd', cat: 'cat-bills', min: 15000, max: 30000 },
    { note: 'Netflix', cat: 'cat-entert', min: 4300, max: 4300 },
    { note: 'Kino', cat: 'cat-entert', min: 3000, max: 8000 },
    { note: 'Przedszkole', cat: 'cat-child', min: 50000, max: 80000 },
    { note: 'Internet', cat: 'cat-bills', min: 5000, max: 7000 },
    { note: 'Żabka', cat: 'cat-food', min: 1000, max: 3000 },
    { note: 'Siłownia', cat: 'cat-entert', min: 12000, max: 15000 },
    { note: 'Fryzjer', cat: 'cat-health', min: 8000, max: 15000 },
  ];

  let id = 1;
  for (let m = 0; m < 3; m++) {
    const month = now.getMonth() - m;
    const year = now.getFullYear() + Math.floor((now.getMonth() - m) / 12);
    const adjMonth = ((month % 12) + 12) % 12;
    const adjYear = month < 0 ? year - 1 : year;

    txs.push({
      id: `dev-tx-income-damian-${m}`,
      familyId: 'dev-family-1',
      walletId: 'dev-wallet-damian',
      userId: 'dev-user-1',
      type: 'income',
      amount: 750000,
      categoryId: 'cat-salary',
      note: 'Wynagrodzenie',
      date: new Date(adjYear, adjMonth, 5),
      isRecurring: true,
      recurringInterval: 'monthly',
      recurringEndDate: null,
      createdAt: new Date(adjYear, adjMonth, 5),
      syncStatus: 'synced',
    });

    txs.push({
      id: `dev-tx-income-ania-${m}`,
      familyId: 'dev-family-1',
      walletId: 'dev-wallet-ania',
      userId: 'dev-user-2',
      type: 'income',
      amount: 580000,
      categoryId: 'cat-salary',
      note: 'Wynagrodzenie',
      date: new Date(adjYear, adjMonth, 10),
      isRecurring: true,
      recurringInterval: 'monthly',
      recurringEndDate: null,
      createdAt: new Date(adjYear, adjMonth, 10),
      syncStatus: 'synced',
    });

    txs.push({
      id: `dev-tx-800plus-${m}`,
      familyId: 'dev-family-1',
      walletId: 'dev-wallet-ania',
      userId: 'dev-user-2',
      type: 'income',
      amount: 80000,
      categoryId: 'cat-800plus',
      note: '800+',
      date: new Date(adjYear, adjMonth, 15),
      isRecurring: true,
      recurringInterval: 'monthly',
      recurringEndDate: null,
      createdAt: new Date(adjYear, adjMonth, 15),
      syncStatus: 'synced',
    });

    // Subskrypcje
    const subs = [
      { name: 'Netflix', amount: 4300, wallet: 'dev-wallet-damian', user: 'dev-user-1' },
      { name: 'Spotify', amount: 2399, wallet: 'dev-wallet-ania', user: 'dev-user-2' },
      { name: 'Internet', amount: 5999, wallet: 'dev-wallet-damian', user: 'dev-user-1' },
    ];
    for (const sub of subs) {
      txs.push({
        id: `dev-tx-${sub.name.toLowerCase()}-${m}`,
        familyId: 'dev-family-1',
        walletId: sub.wallet,
        userId: sub.user,
        type: 'expense',
        amount: sub.amount,
        categoryId: sub.name === 'Internet' ? 'cat-bills' : 'cat-entert',
        note: sub.name,
        date: new Date(adjYear, adjMonth, sub.name === 'Internet' ? 5 : 1),
        isRecurring: true,
        recurringEvery: 1,
        recurringUnit: 'month',
        recurringEndDate: null,
        isPaused: false,
        createdAt: new Date(adjYear, adjMonth, 1),
        syncStatus: 'synced',
      });
    }

    if (m === 0) {
      txs.push({
        id: 'dev-tx-transfer-1', familyId: 'dev-family-1',
        type: 'transfer', walletId: 'dev-wallet-damian', toWalletId: 'dev-wallet-ania',
        userId: 'dev-user-1', amount: 50000, categoryId: null,
        note: 'Na zakupy', date: new Date(adjYear, adjMonth, 8),
        isRecurring: false, isPaused: false, createdAt: new Date(), syncStatus: 'synced',
      });
      txs.push({
        id: 'dev-tx-transfer-2', familyId: 'dev-family-1',
        type: 'transfer', walletId: 'dev-wallet-ania', toWalletId: 'dev-wallet-damian',
        userId: 'dev-user-2', amount: 30000, categoryId: null,
        note: 'Zwrot za paliwo', date: new Date(adjYear, adjMonth, 12),
        isRecurring: false, isPaused: false, createdAt: new Date(), syncStatus: 'synced',
      });
    }

    const numExpenses = 12 + Math.floor(Math.random() * 5);
    for (let i = 0; i < numExpenses; i++) {
      const expense = expenseData[Math.floor(Math.random() * expenseData.length)]!;
      const wallet = wallets[Math.floor(Math.random() * wallets.length)]!;
      const amount = Math.floor(Math.random() * (expense.max - expense.min)) + expense.min;
      txs.push({
        id: `dev-tx-${++id}-${m}`,
        familyId: 'dev-family-1',
        walletId: wallet,
        userId: wallet === 'dev-wallet-damian' ? 'dev-user-1' : 'dev-user-2',
        type: 'expense',
        amount,
        categoryId: expense.cat,
        note: expense.note,
        date: randomDateInMonth(adjYear, adjMonth),
        isRecurring: false,
        recurringInterval: null,
        recurringEndDate: null,
        createdAt: randomDateInMonth(adjYear, adjMonth),
        syncStatus: 'synced',
      });
    }
  }

  return txs.sort((a, b) => b.date.getTime() - a.date.getTime());
}

export const DEV_TRANSACTIONS: DevTransaction[] = generateMockTransactions();

// ── Mock budgets ──────────────────────────────
export const DEV_BUDGETS: DevBudget[] = [
  { id: 'dev-budget-1', familyId: 'dev-family-1', walletId: 'all', categoryId: 'cat-food', amount: 80000, period: 'monthly', alertAt: 80 },
  { id: 'dev-budget-2', familyId: 'dev-family-1', walletId: 'all', categoryId: 'cat-resto', amount: 40000, period: 'monthly', alertAt: 80 },
  { id: 'dev-budget-3', familyId: 'dev-family-1', walletId: 'all', categoryId: 'cat-entert', amount: 30000, period: 'monthly', alertAt: 80 },
  { id: 'dev-budget-4', familyId: 'dev-family-1', walletId: 'all', categoryId: 'cat-child', amount: 100000, period: 'monthly', alertAt: 80 },
];

// ── Mock goals ────────────────────────────────
export const DEV_GOALS: DevGoal[] = [
  {
    id: 'goal-1', familyId: 'dev-family-1', name: 'Wakacje 2026',
    icon: 'travel', color: '#039BE5',
    target: 500000, deadline: new Date('2026-07-01'),
    createdAt: new Date('2026-01-15'),
    deposits: [
      { id: 'dep-1a', amount: 100000, date: new Date('2026-01-20'), type: 'manual', walletId: null },
      { id: 'dep-1b', amount: 80000, date: new Date('2026-02-15'), type: 'wallet', walletId: 'dev-wallet-damian' },
      { id: 'dep-1c', amount: 60000, date: new Date('2026-03-10'), type: 'manual', walletId: null },
    ],
  },
  {
    id: 'goal-2', familyId: 'dev-family-1', name: 'Fundusz awaryjny',
    icon: 'piggy', color: '#43A047',
    target: 1000000, deadline: null,
    createdAt: new Date('2026-02-01'),
    deposits: [
      { id: 'dep-2a', amount: 200000, date: new Date('2026-02-05'), type: 'manual', walletId: null },
      { id: 'dep-2b', amount: 120000, date: new Date('2026-03-05'), type: 'wallet', walletId: 'dev-wallet-damian' },
    ],
  },
  {
    id: 'goal-3', familyId: 'dev-family-1', name: 'Nowy laptop',
    icon: 'shopping', color: '#8E24AA',
    target: 350000, deadline: null,
    createdAt: new Date('2026-01-01'),
    deposits: [
      { id: 'dep-3a', amount: 200000, date: new Date('2026-01-05'), type: 'manual', walletId: null },
      { id: 'dep-3b', amount: 150000, date: new Date('2026-02-01'), type: 'wallet', walletId: 'dev-wallet-damian' },
    ],
  },
];

export function activateDevBridge(): void {
  if (!IS_DEV_MODE) return;
  console.info(
    '%c[DEV MODE] Rodzinka Finance — Dev Bridge active',
    'background:#FFC107;color:#000;padding:4px 8px;border-radius:4px;font-weight:bold',
  );
  console.info('Mock user:', DEV_USER);
  console.info('Mock family:', DEV_FAMILY.name, `(${Object.keys(DEV_FAMILY.members).length} członków)`);
  console.info('Mock transakcje:', DEV_TRANSACTIONS.length);
}
